import { type SingleOnChangeArgs } from 'dibs-elements/exports/SingleSelect';
import { type SocialNetworkList } from '../helpers/sellerApplicationFormValidator';
import { isVariantSellerBusinessDetailsAbTest } from '../helpers/common';

import { type SellerApplicationForm_viewer$data as SalesforceLeadResponse } from './__generated__/SellerApplicationForm_viewer.graphql';

type ApplicationSteps =
    | 'BUSINESS_INTRO'
    | 'BUSINESS_STEP1'
    | 'BUSINESS_STEP2'
    | 'BUSINESS_STEP3'
    | 'BUSINESS_STEP4'
    | 'BUSINESS_STEP5'
    | 'SELECTION_STEP'
    | 'BUSINESS_CONFIRMATION'
    | 'CONSIGNORS_RESTRICTION_MESSAGE';

export const FURNITURE_VINTAGE = 'Furniture - Vintage and Antique';
export const FURNITURE_CONTEMPORARY = 'Furniture - 21st Century (New and Custom)';
export const FINE_ART = 'Fine Art';
export const JEWELRY_ESTATE = 'Jewelry - Estate';
export const JEWELRY_CONTEMPORARY = 'Jewelry - 21st Century (Contemporary)';
export const FASHION_VINTAGE = 'Fashion - Vintage';
export const FASHION_CONTEMPORARY = 'Fashion - Contemporary';

const SF_FURNITURE_VINTAGE = 'Furniture';
const SF_FURNITURE_CONTEMPORARY = '21st Century Furniture';
const SF_FINE_ART = 'Fine Art';
const SF_JEWELRY_ESTATE = 'Jewelry';
const SF_JEWELRY_CONTEMPORARY = '21st Century Jewelry';
const SF_FASHION_VINTAGE = 'Vintage Fashion';
const SF_FASHION_CONTEMPORARY = 'Contemporary Fashion';

type SF_PRIMARY_BUSINESS_FOCUS = [
    typeof SF_FURNITURE_VINTAGE,
    typeof SF_FURNITURE_CONTEMPORARY,
    typeof SF_FINE_ART,
    typeof SF_JEWELRY_ESTATE,
    typeof SF_JEWELRY_CONTEMPORARY,
    typeof SF_FASHION_VINTAGE,
    typeof SF_FASHION_CONTEMPORARY
];

export const primaryBusinessFocusMapToSF = {
    [FURNITURE_VINTAGE]: SF_FURNITURE_VINTAGE,
    [FURNITURE_CONTEMPORARY]: SF_FURNITURE_CONTEMPORARY,
    [FINE_ART]: SF_FINE_ART,
    [JEWELRY_ESTATE]: SF_JEWELRY_ESTATE,
    [JEWELRY_CONTEMPORARY]: SF_JEWELRY_CONTEMPORARY,
    [FASHION_VINTAGE]: SF_FASHION_VINTAGE,
    [FASHION_CONTEMPORARY]: SF_FASHION_CONTEMPORARY,
} as const;

type SFPrimaryBusinessFocus = SF_PRIMARY_BUSINESS_FOCUS[number];

export type PrimaryBusinessFocus = keyof typeof primaryBusinessFocusMapToSF;

type SFPrimaryBusinessFocusMap = { [x in SFPrimaryBusinessFocus]: PrimaryBusinessFocus };

const primaryBusinessFocusMapSFToLocal = Object.entries(primaryBusinessFocusMapToSF).reduce(
    (acc: Partial<SFPrimaryBusinessFocusMap>, [key, value]) => {
        return { ...acc, [value]: key };
    },
    {}
) as SFPrimaryBusinessFocusMap;

type businessTypes = [
    'Auction House',
    'Consignment',
    'Gallery',
    'Showroom',
    'Design Agency',
    'Maker',
    'Artist',
    'Primary Market Gallery',
    'Secondary Market Gallery',
    'Retailer',
    'Secondary Market Platform',
    'Wholesaler',
    'Manufacturer',
    'Reseller'
];

export type BusinessType = businessTypes[number];

export const businessTypeMap: {
    [fieldName in PrimaryBusinessFocus]: BusinessType[];
} = {
    [FURNITURE_VINTAGE]: ['Auction House', 'Gallery', 'Showroom'],
    [FURNITURE_CONTEMPORARY]: ['Design Agency', 'Manufacturer', 'Gallery', 'Maker', 'Showroom'],
    [FINE_ART]: ['Artist', 'Auction House', 'Primary Market Gallery', 'Secondary Market Gallery'],
    [JEWELRY_ESTATE]: ['Auction House', 'Retailer', 'Secondary Market Platform'],
    [JEWELRY_CONTEMPORARY]: [
        'Auction House',
        'Maker',
        'Manufacturer',
        'Retailer',
        'Wholesaler',
        'Secondary Market Platform',
    ],
    [FASHION_VINTAGE]: ['Consignment', 'Reseller'],
    [FASHION_CONTEMPORARY]: ['Maker', 'Retailer', 'Consignment', 'Reseller'],
};

export const estimatedInventorySizeValues = [
    '1-15',
    '16-50',
    '51-100',
    '101-250',
    '251-500',
    '501-1000',
    '1001+',
] as const;

export const soldLastYearValues = ['1-10', '11-50', '51-100', '101-1000', '1001+'] as const;

export const estimatedMonthlyUploadsValues = ['1-15', '16-50', '51-100', '100+'] as const;

export const hoursManagePerWeekValues = ['1-5', '6-10', '11-20', '21+'] as const;

export type Currency = 'USD' | 'GBP' | 'EUR';

export type EstimatedInventorySize = (typeof estimatedInventorySizeValues)[number];

export type SoldLastYear = (typeof soldLastYearValues)[number];

export type EstimatedMonthlyUploads = (typeof estimatedMonthlyUploadsValues)[number];

export type HoursManagePerWeek = (typeof hoursManagePerWeekValues)[number];

export type FormDataContactDetails = {
    firstName: string;
    lastName: string;
    phoneNumber: string;
    businessName: string;
    email: string;
    line1: string;
    country: string;
    zipCode: string;
    city: string;
    state: string;
    eori: string;
    experienceSellingOnline?: boolean;
    hasWebsiteOrSocialMedia?: boolean;
    socialNetworks?: SocialNetworkList;
};

export type FormDataBusinessDetails = {
    primaryBusinessFocus?: PrimaryBusinessFocus;
    businessType?: BusinessType;
    galleryRepresentation?: boolean;
    whereRepresented: string;
    specialty: string;
    estimatedInventorySize?: EstimatedInventorySize;
    averageItemPriceCurrency?: Currency;
    averageItemPriceValue: string;
    numSoldLastYear?: SoldLastYear;
    estimatedMonthlyUploads?: EstimatedMonthlyUploads;
    isSellerOnOtherMarketPlace?: boolean;
    otherMarketPlaceDetails: string;
    hoursManagePerWeek?: HoursManagePerWeek;
};

const FURNITURE = 'Furniture' as const;
const JEWELRY = 'Jewelry' as const;
const ART = 'Art' as const;
const FASHION = 'Fashion' as const;

type verticals = [typeof FURNITURE, typeof JEWELRY, typeof ART, typeof FASHION];

const verticalMap = {
    [FURNITURE_VINTAGE]: FURNITURE,
    [FURNITURE_CONTEMPORARY]: FURNITURE,
    [FINE_ART]: ART,
    [JEWELRY_ESTATE]: JEWELRY,
    [JEWELRY_CONTEMPORARY]: JEWELRY,
    [FASHION_VINTAGE]: FASHION,
    [FASHION_CONTEMPORARY]: FASHION,
} as const;

type VerticalType = verticals[number];

export type BusinessItem = {
    title: string;
    category: string;
    subCategory: string;
    description: string;
    creatorArtistDesigner?: SingleOnChangeArgs;
    price: string;
    period: string;
    condition: string;
    imageFiles: File[];
};

type FormDataBusinessInventory = {
    items: Array<Partial<BusinessItem>>;
};

export const SINGLE_LOCATION = 'Single location';
export const MULTIPLE_LOCATIONS = 'Multiple locations';

export type FormDataAdditionalDetails = {
    preferredLanguage: string;
    associations: string;
    fairs: string;
    locations?: typeof SINGLE_LOCATION | typeof MULTIPLE_LOCATIONS;
    locationsWithinSelectedCountry?: boolean;
    ownInventory?: boolean;
    consignmentBasis?: boolean;
};

export type BusinessDetailsKeys = keyof FormDataBusinessDetails;

export type ContactDetailsKeys = keyof FormDataContactDetails;

export type AdditionalDetailsKeys = keyof FormDataAdditionalDetails;

export type ReducerState = {
    step?: ApplicationSteps;
    leadId: string;
    vertical: VerticalType;
    formDataContactDetails: FormDataContactDetails;
    formDataBusinessDetails: FormDataBusinessDetails;
    formDataBusinessInventory: FormDataBusinessInventory;
    formDataAdditionalDetails: FormDataAdditionalDetails;
};

export type ReducerActions =
    | {
          type: 'SET_STEP';
          step?: ApplicationSteps;
      }
    | {
          type: 'SET_CREATED_LEAD_ID';
          leadId: string;
          businessFocus?: PrimaryBusinessFocus;
      }
    | { type: 'SET_FORM_CONTACT_DETAILS'; formDataContactDetails: Partial<FormDataContactDetails> }
    | {
          type: 'SET_FORM_BUSINESS_DETAILS';
          formDataBusinessDetails: Partial<FormDataBusinessDetails>;
      }
    | {
          type: 'SET_BUSINESS_ITEM';
          index: number;
          item: Partial<BusinessItem>;
      }
    | {
          type: 'SET_BUSINESS_ITEM_FIELD';
          index: number;
          fieldValue: Partial<BusinessItem>;
      }
    | {
          type: 'REMOVE_BUSINESS_ITEM';
          index: number;
      }
    | {
          type: 'SET_FORM_ADDITIONAL_DETAILS';
          formDataAdditionalDetails: Partial<FormDataAdditionalDetails>;
      };

export const blankState: ReducerState = {
    step: undefined,
    leadId: '',
    vertical: ART,
    formDataContactDetails: {
        businessName: '',
        firstName: '',
        lastName: '',
        phoneNumber: '',
        email: '',
        line1: '',
        country: '',
        zipCode: '',
        city: '',
        state: '',
        eori: '',
        experienceSellingOnline: undefined,
        hasWebsiteOrSocialMedia: undefined,
        socialNetworks: undefined,
    },
    formDataBusinessDetails: {
        primaryBusinessFocus: undefined,
        businessType: undefined,
        galleryRepresentation: undefined,
        whereRepresented: '',
        specialty: '',
        estimatedInventorySize: undefined,
        averageItemPriceCurrency: undefined,
        averageItemPriceValue: '',
        numSoldLastYear: undefined,
        estimatedMonthlyUploads: undefined,
        isSellerOnOtherMarketPlace: undefined,
        otherMarketPlaceDetails: '',
        hoursManagePerWeek: undefined,
    },
    formDataBusinessInventory: {
        items: [],
    },
    formDataAdditionalDetails: {
        preferredLanguage: '',
        associations: '',
        fairs: '',
        locations: undefined,
        locationsWithinSelectedCountry: undefined,
        ownInventory: undefined,
        consignmentBasis: undefined,
    },
};

export const createInitialState = (viewer: SalesforceLeadResponse): ReducerState => {
    if (!viewer?.readFromSF) {
        return blankState;
    }
    const {
        recordId,
        company,
        firstName,
        lastName,
        phone,
        email,
        country,
        state: addressState,
        city,
        street,
        postalCode,
        primaryBusinessFocus: primaryBusinessFocusSF,
        eori,
        hasExperienceSellingOnline,
        businessType,
        galleryRepresentation,
        whereRepresented,
        specialty,
        estimatedInventorySize,
        currencyISOCode,
        averageItemPrice,
        numSoldLastYear,
        estimatedMonthlyUploads,
        isSellerOnOtherMarketPlace,
        otherMarketPlaceDetails,
        hoursManagePerWeek,
        instagram,
        facebook,
        website,
        status,
    } = viewer.salesforceSellerLead || {};
    const primaryBusinessFocus: PrimaryBusinessFocus | undefined = primaryBusinessFocusSF
        ? primaryBusinessFocusMapSFToLocal[primaryBusinessFocusSF as SFPrimaryBusinessFocus]
        : undefined;

    let currentStep: ApplicationSteps = !primaryBusinessFocusSF
        ? 'BUSINESS_STEP2'
        : 'BUSINESS_STEP3';

    if (
        estimatedMonthlyUploads ||
        (primaryBusinessFocusSF && isVariantSellerBusinessDetailsAbTest)
    ) {
        currentStep = 'BUSINESS_STEP4';
    }

    if (businessType && status !== 'Needs More Info' && status !== 'Open') {
        currentStep = 'BUSINESS_CONFIRMATION';
    }

    const socialNetworks: SocialNetworkList = [];
    if (instagram) {
        socialNetworks.push({ network: 'Instagram', socialUrl: instagram });
    }
    if (facebook) {
        socialNetworks.push({ network: 'Facebook', socialUrl: facebook });
    }
    if (website) {
        socialNetworks.push({ network: 'Website', socialUrl: website });
    }
    const initialState: ReducerState = recordId
        ? {
              ...blankState,
              step: currentStep,
              leadId: recordId || '',
              vertical: primaryBusinessFocus ? verticalMap[primaryBusinessFocus] : ART,
              formDataContactDetails: {
                  firstName: firstName || '',
                  lastName: lastName || '',
                  phoneNumber: phone || '',
                  businessName: company || '',
                  email: email || '',
                  line1: street || '',
                  country: country || '',
                  zipCode: postalCode || '',
                  city: city || '',
                  state: addressState || '',
                  eori: eori || '',
                  experienceSellingOnline: !!hasExperienceSellingOnline,
                  hasWebsiteOrSocialMedia: !!instagram || !!facebook || !!website || undefined,
                  socialNetworks,
              },
              formDataBusinessDetails: {
                  primaryBusinessFocus: primaryBusinessFocus || undefined,
                  businessType: (businessType as BusinessType) || undefined,
                  galleryRepresentation: galleryRepresentation || undefined,
                  whereRepresented: whereRepresented || '',
                  specialty: specialty || '',
                  estimatedInventorySize:
                      (estimatedInventorySize as EstimatedInventorySize) || undefined,
                  averageItemPriceCurrency: (currencyISOCode as Currency) || undefined,
                  averageItemPriceValue: averageItemPrice || '',
                  numSoldLastYear: (numSoldLastYear as SoldLastYear) || undefined,
                  estimatedMonthlyUploads:
                      (estimatedMonthlyUploads as EstimatedMonthlyUploads) || undefined,
                  isSellerOnOtherMarketPlace: isSellerOnOtherMarketPlace ?? undefined,
                  otherMarketPlaceDetails: otherMarketPlaceDetails || '',
                  hoursManagePerWeek: (hoursManagePerWeek as HoursManagePerWeek) || undefined,
              },
          }
        : blankState;
    return initialState;
};

export function reducer(state: ReducerState, action: ReducerActions): ReducerState {
    switch (action.type) {
        case 'SET_STEP':
            return { ...state, step: action.step };
        case 'SET_CREATED_LEAD_ID':
            return {
                ...state,
                leadId: action.leadId,
            };
        case 'SET_FORM_CONTACT_DETAILS': {
            return {
                ...state,
                formDataContactDetails: {
                    ...state.formDataContactDetails,
                    ...action.formDataContactDetails,
                },
            };
        }
        case 'SET_FORM_BUSINESS_DETAILS': {
            const vertical = action.formDataBusinessDetails?.primaryBusinessFocus
                ? verticalMap[action.formDataBusinessDetails.primaryBusinessFocus]
                : null;
            return {
                ...state,
                ...(vertical && { vertical }),
                formDataBusinessDetails: {
                    ...state.formDataBusinessDetails,
                    ...action.formDataBusinessDetails,
                },
            };
        }
        case 'SET_BUSINESS_ITEM': {
            const { index, item } = action;
            const items = state.formDataBusinessInventory.items;
            items[index] = item;
            return {
                ...state,
                formDataBusinessInventory: {
                    items,
                },
            };
        }
        case 'SET_BUSINESS_ITEM_FIELD': {
            const { index, fieldValue } = action;
            const items = state.formDataBusinessInventory.items;
            const item = { ...items[index], ...fieldValue };
            items[index] = item;
            return {
                ...state,
                formDataBusinessInventory: {
                    items,
                },
            };
        }
        case 'REMOVE_BUSINESS_ITEM': {
            const { index } = action;
            const items = state.formDataBusinessInventory.items.filter(
                (_, itemIndex) => itemIndex !== index
            );
            return {
                ...state,
                formDataBusinessInventory: {
                    items,
                },
            };
        }
        case 'SET_FORM_ADDITIONAL_DETAILS': {
            return {
                ...state,
                formDataAdditionalDetails: {
                    ...state.formDataAdditionalDetails,
                    ...action.formDataAdditionalDetails,
                },
            };
        }
        default:
            return state;
    }
}
